import React, {
  FunctionComponent,
  ReactElement,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Field } from 'react-final-form'
import { useIntl } from 'react-intl'
import { connect, ConnectedProps, useSelector } from 'react-redux'
import { useNavigate, useOutletContext } from 'react-router'
import { Outlet, useLocation } from 'react-router-dom'

import { format, zonedTimeToUtc } from 'date-fns-tz'
import { get, isBoolean, isEmpty, isEqual, isNil, merge, noop } from 'lodash-es'
import { bindActionCreators } from 'redux'
import styled, { css, useTheme } from 'styled-components/native'

import { AlertBannerTypes } from '@lyrahealth-inc/shared-app-logic/src/features/alerts/AlertBannerTypes'
import {
  getAppointmentTimeslot,
  isSessionOlderThan90Days,
} from '@lyrahealth-inc/shared-app-logic/src/features/appointments/utils'
import {
  extendMetadata,
  getClientPreferredNameIfExists,
  isFieldDisplayed,
} from '@lyrahealth-inc/shared-app-logic/src/features/assignments/utils'
import { isCustomerSoleraCoachingTherapyMeds } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/customerUtils'
import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { Programs } from '@lyrahealth-inc/shared-app-logic/src/features/programConfigs/programConfigs'
import { attendanceValues } from '@lyrahealth-inc/shared-app-logic/src/features/sessions/endOfSession/endOfSessionConstants'
import type { endOfSessionMetadataValues } from '@lyrahealth-inc/shared-app-logic/src/features/sessions/endOfSession/endOfSessionMetadata'
import {
  getLTSessionTypeAndRate,
  isInitialEvaluationSessionType,
} from '@lyrahealth-inc/shared-app-logic/src/features/sessions/utils'
import { Appointment } from '@lyrahealth-inc/shared-app-logic/src/models/appointment/Appointment'
import { Episode } from '@lyrahealth-inc/shared-app-logic/src/models/episodes/Episodes'
import {
  FieldSchema,
  FieldValue,
  FormMetadata,
} from '@lyrahealth-inc/shared-app-logic/src/models/formMetadata/FormMetadata'
import { NoteStatusStates, NoteTypes } from '@lyrahealth-inc/shared-app-logic/src/models/notes/Notes'
import { ProgramNames } from '@lyrahealth-inc/shared-app-logic/src/models/programs/Programs'
import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { LoadingIndicator } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/LoadingIndicator'
import { Link } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/link/Link'
import { PrimaryButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/primaryButton/PrimaryButton'
import { Title } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/title/Title'
import { useFetcher } from '@lyrahealth-inc/ui-core-crossplatform/src/hooks/useFetcher'
import { AlertBanner } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/alertBanner/AlertBanner'
import { EligibilityErrorBanner } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/eligibilityErrorBanner/EligibilityErrorBanner'
import { InlineWarningBanner } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/inlineWarningBanner/InlineWarningBanner'
import { TabbedExpandingSidePanel } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/sidePanel/TabbedExpandingSidePanel'
import { ClientDetailsHeader } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/clientDetailsHeader/ClientDetailsHeader'
import { FormBody, FormBodyCustomStyles } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/formBody/FormBody'
import { BodyTextSize, TitleSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { ThemeType } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeProvider'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import { setAppointment } from './data/appointmentsSlice'
import { setToastContent } from '../../data/ltToastAutoActions'
import { useClientAppointmentsData } from './data/hooks/useClientAppointmentsData'
import { useCustomersForClient } from './data/hooks/useCustomersForClient'
import { useSelectedClientData } from './data/hooks/useSelectedClientData'
import { useEndOfSessionRequests } from './hooks/useEndOfSessionRequests'
import { useSessionLimitInfo } from './hooks/useSessionLimitInfo'
import { actions, mixpanelEvents } from '../../../../../mixpanel/mixpanelConstants'
import { track } from '../../../../../mixpanel/mixpanelTracking'
import { applySpecialtyCareAttestation } from '../../../common/components/healthPlanInfo/getSpecialtyCareAttestationExtension'
import ICD10sField, { ICD10sVariant } from '../../../common/components/healthPlanInfo/ICD10sField'
import ICD10sFormMetadata from '../../../common/components/healthPlanInfo/ICD10sFormMetadata'
import {
  BCC_ROLES,
  coachingSessionTypesSet,
  PAYMENT_TYPES,
  programConfig as PROGRAM_CONFIG,
  ProgramConfig,
  ROLES,
} from '../../../common/constants/appConstants'
import { CLIENT_ACTIVITIES, CLIENT_NEW_NOTE, CLIENTS_SESSIONS } from '../../../common/constants/routingConstants'
import {
  getItemsErrors,
  hasRole,
  isECDCProvider,
  isOneArrayItemTrue,
  logToSentry,
  resolveDAEngageProgramName,
} from '../../../common/utils/utils'
import { getAuthUser } from '../../../data/auth/authSelectors'
import { getHealthPlanICD10s } from '../../../data/healthPlan/healthPlanSelectors'
import { getHPEligibility, getICD10s } from '../../../data/healthPlanAutoActions'
import { getClientCurrentSessionCount } from '../../../data/lyraTherapy/clientSelectors'
import { getLyraTherapyContentsData } from '../../../data/lyraTherapy/contentSelectors'
import { getPaymentsDataAllData } from '../../../data/payments/paymentsSelectors'
import {
  getRequestPaymentDiffPricingEnabled,
  getRequestPaymentDiffPricingModifier,
  getRequestPaymentRates,
} from '../../../data/requestPayment/requestPaymentSelectors'
import { useAppDispatch } from '../../../data/store'
import { ACTIVE_ALERT_HEIGHT, JOIN_BANNER_HEIGHT, useAllBannerVisibility } from '../../../hooks/useAllBannerVisibility'
import { useEapEligibilityError } from '../../../hooks/useEapEligibilityError'
import { useIsEngageDAProvider } from '../../../hooks/useIsEngageDAProvider'
import { getPayments } from '../../../payments/dashboard/data/paymentsDashboardActions'
import { useUpdateTransferMutation } from '../../../payments/data/transfersApi'
import { populateInitialValues } from '../../clientNotes/ClientNotes'
import {
  SIDEBAR_TRANSITION_TIME,
  useGetTabbedExpandingSidePanelContent,
} from '../../clientNotes/hooks/useGetTabbedExpandingSidePanelContent'
import { getOriginRouteDestination } from '../../clientNotes/utils/noteNavigationUtils'
import { getContentsByProgramId } from '../../data/ltContentsAutoActions'
import { useSelectedEpisodeForClient } from '../../episodes/data/hooks/useSelectedEpisodeForClient'
import { BillingImplicationsMessage } from '../../sessions/endOfSession/BillingImplicationsMessage'
import { EndOfSessionDAPreText } from '../../sessions/endOfSession/EndOfSessionDAPreText'
import { useCloseSessionMetadata } from '../../sessions/endOfSession/hooks/useCloseSessionMetadata'
import { useCloseSessionSubmit } from '../../sessions/endOfSession/hooks/useCloseSessionSubmit'

const AllAlertsContainer = styled.View<{ videoSessionOpen: boolean }>(({ videoSessionOpen }) => ({
  marginTop: videoSessionOpen ? '200px' : '0px',
}))

const StickyHeaderContainer = styled.View<{ theme: ThemeType; topOffset: number }>(({ topOffset }) => ({
  position: 'sticky',
  top: `${topOffset}px`,
  zIndex: 11,
  width: '100%',
}))

const WholeContainer = styled.View({
  flexDirection: 'row',
  flexGrow: 1,
})

const LeftSideContainer = styled.View({
  flexGrow: 1,
})

const RightSideContainer = styled.View<{ topOffset: number }>(({ topOffset }) => ({
  position: 'sticky',
  top: `${topOffset}px`,
  height: `calc(100vh - ${topOffset}px)`,
  alignSelf: 'flex-start',
  zIndex: 10,
}))

const SubmissionAlertContainer = styled.View({
  margin: 0,
  padding: 0,
})

const OutOfOrderAlertContainer = styled.View<{ submissionAlertBanner: boolean }>(({ submissionAlertBanner }) => ({
  marginTop: submissionAlertBanner ? '4px' : '0px',
}))

const InnerContainer = styled.View<{
  isFormProcessing?: boolean
}>(({ isFormProcessing }) => ({
  width: '624px',
  margin: 'auto',
  marginTop: `${isFormProcessing ? '150' : '45'}px`,
}))

const Subheader = styled(Title)<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  marginBottom: spacing['24px'],
}))

const NotificationBannerContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  marginTop: spacing['32px'],
  marginBottom: spacing['24px'],
}))

const CloseSessionWarningText = styled(BodyText)<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  marginTop: spacing['16px'],
}))

const SubmitButton = styled(PrimaryButton)<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  marginRight: spacing['16px'],
  marginLeft: spacing['16px'],
}))

type CloseSessionError = {
  message?: string
  response?: { data?: { message?: string } }
}

const EndOfSession: FunctionComponent<EndOfSessionProps> = ({
  actions: { getHPEligibility, getICD10s, getPayments, getContentsByProgramId },
}: any) => {
  const dispatch = useAppDispatch()
  const { colors } = useTheme()
  const location = useLocation()
  const {
    appointment: locationAppointment,
    draftNote,
    previousCharge,
    showTransferError,
    noteAlreadyCompleted,
  } = location.state || {}
  const originRouteFromState = get(location.state, 'originRoute') as string | undefined
  const [originallyTargetedAppointment, setOriginallyTargetedAppointment] = useState(locationAppointment)
  const user = useSelector(getAuthUser)
  const submitRef = useRef(noop)
  const endOfSessionFormRef = useRef<any>(null)
  const { data: clientDetails } = useSelectedClientData()
  const { appointment, setShowSubNav } = useOutletContext<{
    appointment: Appointment
    setShowSubNav: (visibility: boolean) => void
  }>()
  const appointmentIdMismatch =
    !isNil(originallyTargetedAppointment) &&
    !isEmpty(originallyTargetedAppointment) &&
    appointment.appointmentId !== originallyTargetedAppointment.appointmentId

  useLayoutEffect(() => {
    setShowSubNav(false)
  })
  const navigate = useNavigate()
  const intl = useIntl()
  const [updateTransfer] = useUpdateTransferMutation()
  const [showClosingSessionsOutOfOrderAlert, setShowClosingSessionsOutOfOrderAlert] = useState(false)
  const [closureFormValues, setClosureFormValues] = useState<endOfSessionMetadataValues | null>(null)
  const [alertBanner, setAlertBanner] = useState<ReactElement | null>(null)
  const [isTabbedExpandingSidePanelOpen, setIsTabbedExpandingSidePanelOpen] = useState(false)
  const sessionsRates = useSelector(getRequestPaymentRates)
  const icd10Codes = useSelector(getHealthPlanICD10s)
  const {
    showEnhancedLCxDiagnosis,
    notesSidePanel,
    isECDCEnabled,
    isDAProviderMigrated,
    shouldUseInternationalCoachingSesssionType,
    isSelfPayEnabled,
  } = useFlags()
  const { data: clientDetailsSelectedEpisode } = useSelectedEpisodeForClient() as { data: Episode | undefined }
  const { hasAnySessionBanner, hasVideoSessionBanner, hasActiveAlerts } = useAllBannerVisibility()
  const topOffset = hasAnySessionBanner ? JOIN_BANNER_HEIGHT : 0
  const activeAlertTopOffset = hasActiveAlerts ? ACTIVE_ALERT_HEIGHT : 0
  const selectedEpisode = clientDetailsSelectedEpisode
  const contents = useSelector(getLyraTherapyContentsData)
  const programConfig: ProgramConfig | undefined = selectedEpisode
    ? PROGRAM_CONFIG[selectedEpisode.program_name]
    : undefined

  const pastPayments = useSelector(getPaymentsDataAllData)
  const currentSessionCount = useSelector(getClientCurrentSessionCount) || 0

  const differentialPricingEnabled = useSelector(getRequestPaymentDiffPricingEnabled)
  const differentialPricingModifier = useSelector(getRequestPaymentDiffPricingModifier)
  const customers = useCustomersForClient()

  const enhancedLCxDiagnosisEnabled = programConfig?.enhancedLCxDiagnosisEnabled && showEnhancedLCxDiagnosis
  const shouldShowGuardianConsent =
    selectedEpisode?.program_name === ProgramNames.TeensTherapy && currentSessionCount === 1

  const tabbedExpandingSidePanelContent = useGetTabbedExpandingSidePanelContent({
    showAISummary: false,
    noteType: 'EndOfSession',
  })
  const isProviderEngageDA = useIsEngageDAProvider()

  // For DA Engage providers, use the originally targeted appointment (the one they clicked)
  // For LC providers, use the appointment from outlet context (first unclosed session)
  const appointmentToClose: Appointment =
    isProviderEngageDA && !isEmpty(originallyTargetedAppointment) ? originallyTargetedAppointment : appointment

  // ECDC is enhanced clinical data collection, more information here:
  // https://lyrahealth.atlassian.net/wiki/spaces/DACN/pages/2551972828/Enhanced+Clinical+Data+Collection+ECDC
  // when this is true then we will capture more details, e.g., primary_condition, rather than just diagnosis.
  const shouldDAProviderUseECDC =
    isECDCEnabled && isProviderEngageDA && (isECDCProvider(user!) || hasRole(user!.roles, ROLES.PAYMENTS_ADMIN))

  const useEnhancedDiagnosisCapture = enhancedLCxDiagnosisEnabled || shouldDAProviderUseECDC
  const isProviderCoach = hasRole(user?.roles ?? [], BCC_ROLES)

  const isEndOfSessionSidePanelEnabled = Boolean(notesSidePanel?.endofsession)

  useEffect(() => {
    if (appointmentIdMismatch) {
      setShowClosingSessionsOutOfOrderAlert((prevState) => {
        dispatch(setAppointment(appointment))
        return prevState || true
      })

      setOriginallyTargetedAppointment(locationAppointment)
      track({
        event: mixpanelEvents.BUTTON_PRESS,
        action: actions.ATTEMPT_TO_CLOSE_SESSION_OUT_OF_ORDER,
        details: {
          appointmentId: appointment?.appointmentId,
        },
      })
    }
  }, [appointment, appointmentIdMismatch, dispatch, locationAppointment])

  useEffect(() => {
    if (showTransferError) {
      dispatch(
        setToastContent({
          text: 'Unable to load previous session details. Please enter details manually.',
          toastType: 'warning',
          id: 'EndOfSession-get-transfers-error',
        }),
      )
    }
  }, [showTransferError, dispatch])

  const { data: allAppointments } = useClientAppointmentsData()

  const {
    employer,
    gender,
    last_name: lastName,
    first_name: firstName,
    full_name: fullName,
    date_of_birth: dateOfBirth,
    id: clientId,
    lyra_code: lyraCode,
  } = clientDetails ?? {}

  const {
    showLowSessionsWarning,
    showHealthPlanLimitInfo,
    lowSessionsRemainingText,
    healthPlanLimitInfoText,
    showLowLicenseSessionWarning,
    lowLicenseSessionRemainingText,
  } = useSessionLimitInfo({ clientDetails, programName: selectedEpisode?.program_name })

  const isNonAttendedSession = closureFormValues?.attendance === attendanceValues.NO_SHOW
  const isLateClientCancellation =
    closureFormValues?.changeReason === 'clientInitiatedLate' ||
    (closureFormValues?.changeReason === 'clientCancel' && closureFormValues?.changeNotice === 'clientInitiatedLate')

  const sessionDetails = useMemo(() => {
    const modality =
      isProviderEngageDA && !isNonAttendedSession && !isLateClientCancellation
        ? (() => {
            try {
              return JSON.parse(closureFormValues?.modality ?? '{}')?.type ?? ''
            } catch {
              return ''
            }
          })()
        : (appointmentToClose?.meetingFormat ?? '')

    return selectedEpisode && selectedEpisode.program_name
      ? getLTSessionTypeAndRate({
          programName: resolveDAEngageProgramName(selectedEpisode.program_name, isProviderEngageDA),
          sessionNumber: appointmentToClose.sessionNumber,
          sessionsRates: sessionsRates ?? {},
          dateOfBirth: dateOfBirth,
          modality,
          differentialPricingEnabled: differentialPricingEnabled ?? false,
          differentialPricingModifier: differentialPricingModifier ?? 0,
          isInternationalCoachingSessionTypeEnabled: shouldUseInternationalCoachingSesssionType,
          clientCountry: clientDetails?.country,
          bookedSessionType: appointmentToClose.sessionType,
          suggestedSessionType: appointmentToClose.suggestedSessionType,
        })
      : null
  }, [
    isProviderEngageDA,
    appointmentToClose?.meetingFormat,
    appointmentToClose.sessionNumber,
    appointmentToClose.sessionType,
    appointmentToClose.suggestedSessionType,
    selectedEpisode,
    sessionsRates,
    dateOfBirth,
    differentialPricingEnabled,
    differentialPricingModifier,
    shouldUseInternationalCoachingSesssionType,
    clientDetails?.country,
    closureFormValues?.modality,
    isNonAttendedSession,
    isLateClientCancellation,
  ])

  const { sessionType } = sessionDetails ?? {}
  const healthPlanEligibilityPayload = {
    ...(lyraCode && { lyra_code: lyraCode }),
    visit_date: appointmentToClose.startDate,
    provider_lyra_id: user?.id,
    session_type: sessionType ?? null,
    company: employer,
    gender,
    date_of_birth: dateOfBirth,
    last_name: lastName,
    first_name: firstName,
    ...(clientId && { patient_lyra_id: clientId }),
    appointment_id: appointmentToClose.appointmentId,
  }
  const {
    isLoadingHPEligibility,
    isLoadingSessionMetadata,
    eligibilityObj,
    hasFetchedHPEligibility,
    hasFetchedSessionMetadata,
  } = useEndOfSessionRequests({
    getHPEligibility,
    getICD10s,
    getContentsByProgramId,
    healthPlanEligibilityPayload,
    sessionType,
    programId: selectedEpisode?.program_id,
    shouldFetchContents: !!(isProviderEngageDA && selectedEpisode?.program_id),
  })

  const [isLoadingPayments] = useFetcher(
    [[getPayments, { page: 0, limit: 20, providerId: user?.id, patientId: clientId }]],
    [user?.id, clientId],
  )

  const eligibilityError = useEapEligibilityError({
    programName: selectedEpisode?.program_name,
  })

  const isLoading =
    isLoadingHPEligibility ||
    isLoadingSessionMetadata ||
    isLoadingPayments ||
    isEmpty(icd10Codes) ||
    !hasFetchedHPEligibility ||
    !hasFetchedSessionMetadata

  const {
    speciality_care_attestation_status: specialtyCareAttestationStatus,
    next_session_uses_health_plan: nextSessionUsesHealthPlan,
    payment_type: paymentType,
  } = eligibilityObj ?? {}

  const isUsingSelfPay =
    isSelfPayEnabled &&
    paymentType === PAYMENT_TYPES.SELF_PAY &&
    Programs.getProgramConfigByName(selectedEpisode?.program_name)?.selfPayEligible

  const isNotCoachingTypeSession = !isNil(sessionType) && !coachingSessionTypesSet.has(sessionType)

  // Solera coaching + therapy + meds customers: therapy and meds are configured as EAP supported, but Solera submits
  // the sessions to the client's health plan, so LCx providers must provide ICD-10 codes for therapy and meds sessions.
  // DA providers are handled in the request payment workflow.
  const shouldShowICD10ForSolera =
    !isProviderEngageDA &&
    !nextSessionUsesHealthPlan &&
    !isUsingSelfPay &&
    isCustomerSoleraCoachingTherapyMeds(employer) &&
    isNotCoachingTypeSession

  // CRITICAL: nextSessionUsesHealthPlan should ONLY be true or false (boolean), never undefined.
  // If it's undefined, the API failed or returned an incomplete response.
  // Show an error and block form submission to prevent closing sessions without proper validation.
  const didHPEligibilityAPIFail = hasFetchedHPEligibility && nextSessionUsesHealthPlan === undefined

  useEffect(() => {
    if (didHPEligibilityAPIFail) {
      setAlertBanner(
        <AlertBanner
          dismissHandler={() => setAlertBanner(null)}
          includeX={true}
          key={`alertHPEligibilityFailed`}
          show={true}
          style={AlertBannerTypes.DANGER}
          contents={
            'Unable to verify health plan eligibility. Please refresh the page and try again. If this error persists, contact support.'
          }
        />,
      )
    } else {
      setAlertBanner((current) => (current?.key === 'alertHPEligibilityFailed' ? null : current))
    }
  }, [didHPEligibilityAPIFail])

  const { submitForm: closeSessionSubmit, isFormProcessing } = useCloseSessionSubmit({
    appointmentToClose,
    selectedEpisode,
    originallyTargetedAppointment,
    sessionDetails,
    isUsingSelfPay: !!isUsingSelfPay,
    isCustomerIcd10Required: shouldShowICD10ForSolera,
    useEnhancedDiagnosisCapture,
    shouldShowGuardianConsent,
    programConfig,
  })

  const { metadata: closeSessionMetadata } = useCloseSessionMetadata({
    appointmentToClose,
    selectedEpisode,
    isProviderEngageDA,
    isProviderCoach,
    useEnhancedDiagnosisCapture,
    shouldShowGuardianConsent,
    nextSessionUsesHealthPlan: !!nextSessionUsesHealthPlan,
    calculatedSessionRate: sessionDetails?.sessionRate ?? 0,
    previousCharge,
  })

  let metadata = closeSessionMetadata

  // Coaches should not be asked to provide ICD10 and verify copay consent for coaching health plan sessions.
  // Default values will be used when the health plan claim is generated.
  // For self-pay: if sessionType is unknown/undefined, assume it's NOT a coaching session to be safe
  const shouldShowICD10ForHealthPlan = nextSessionUsesHealthPlan && isNotCoachingTypeSession
  const shouldShowICD10ForSelfPay = isUsingSelfPay && (isNotCoachingTypeSession || isNil(sessionType))

  if (shouldShowICD10ForHealthPlan || shouldShowICD10ForSelfPay || shouldShowICD10ForSolera) {
    metadata = extendMetadata(metadata, ICD10sFormMetadata.meta_data as FormMetadata)
  }
  const { label: companyLabel } = customers?.find(({ value }: any) => value === employer) || {}

  const getICD10sVariant = (): ICD10sVariant => {
    if (shouldShowICD10ForSolera && companyLabel) {
      return { type: 'customer', customerName: companyLabel }
    }
    if (isUsingSelfPay && isNotCoachingTypeSession) {
      return {
        type: 'selfPay',
        clientFirstName: clientDetails?.first_name ?? '',
        pronouns: clientDetails?.pronouns ?? [],
      }
    }
    return { type: 'healthPlan' }
  }
  const icd10sVariant = getICD10sVariant()

  metadata = applySpecialtyCareAttestation(metadata, {
    specialtyCareAttestationStatus,
    companyLabel,
    firstName,
    fullName,
    pastHealthPlanPayments: pastPayments?.filter((pastPayment: any) => pastPayment.is_health_plan),
  })
  useEffect(() => {
    if (isLoading) return

    // If the metadata has initial values, use those. Otherwise, use the closure form values.
    if (!isNil(metadata.initialValues) && !isEqual(metadata.initialValues, closureFormValues)) {
      const initialValuesKeyCount = Object.keys(metadata.initialValues || {}).length
      const closureFormValuesKeyCount = Object.keys(closureFormValues || {}).length
      const valueToUse = initialValuesKeyCount > closureFormValuesKeyCount ? metadata.initialValues : closureFormValues
      setClosureFormValues(valueToUse as endOfSessionMetadataValues)
    }
  }, [closureFormValues, isLoading, metadata.initialValues])

  const submitForm = async () => {
    if (!closureFormValues) return

    // Block form submission if health plan eligibility API failed
    if (didHPEligibilityAPIFail) {
      setAlertBanner(
        <AlertBanner
          dismissHandler={() => setAlertBanner(null)}
          includeX={true}
          key={`alertHPEligibilityFailedSubmit`}
          show={true}
          style={AlertBannerTypes.DANGER}
          contents={
            'Cannot close session: Unable to verify health plan eligibility. Please refresh the page and try again. If this error persists, contact support.'
          }
        />,
      )
      return
    }

    try {
      const result = await closeSessionSubmit(closureFormValues)
      if (!result) return

      const {
        charge,
        chargeId,
        isPendingNoteCharge,
        shouldCharge,
        hasNoShowTransfersError,
        isClosingOutOfOrder: closedOutOfOrder,
      } = result

      if (closedOutOfOrder && !isProviderEngageDA) {
        setShowClosingSessionsOutOfOrderAlert(true)
      } else {
        setShowSubNav(true)
        const attendance = closureFormValues?.attendance
        const shouldContinueToNote = isProviderEngageDA && (shouldCharge || attendance === attendanceValues.INELIGIBLE)
        if (noteAlreadyCompleted) {
          let acceptChargeFailed = false
          if (chargeId && isPendingNoteCharge && clientId) {
            try {
              await updateTransfer({ chargeId, patient_lyra_id: clientId, status: 'accept' }).unwrap()
            } catch (error) {
              acceptChargeFailed = true
              logToSentry('daNotesWorkflow', {
                message: 'DA notes workflow: failed to accept charge after closing a session with a completed note',
                error: error instanceof Error ? { message: error.message } : { error },
                chargeId,
                appointmentId: appointmentToClose.appointmentId,
                clientId,
              })
            }
          }
          if (acceptChargeFailed) {
            dispatch(
              setToastContent({
                text: intl.formatMessage(
                  {
                    defaultMessage:
                      "Session {sessionNumber} closed, but we couldn't finish processing the payment. Please contact support.",
                    description:
                      'Error toast shown when a DA session with an already submitted note is closed but its charge could not be accepted',
                  },
                  { sessionNumber: appointmentToClose.sessionNumber },
                ),
                id: 'EndOfSession-accept-charge-error',
                toastType: 'error',
              }),
            )
          } else if (chargeId) {
            dispatch(
              setToastContent({
                text: intl.formatMessage(
                  {
                    defaultMessage: 'Session {sessionNumber} closed and payment is processing.',
                    description:
                      'Success toast shown when a DA session with an already submitted note is closed and billed',
                  },
                  { sessionNumber: appointmentToClose.sessionNumber },
                ),
                id: 'EndOfSession-accept-charge-success',
                toastType: 'success',
              }),
            )
          }
          navigate(
            getOriginRouteDestination({ originRoute: originRouteFromState, isEngageDAProvider: isProviderEngageDA }),
          )
        } else if (shouldContinueToNote) {
          const { INITIAL_NOTE, SESSION_NOTE } =
            Programs.getProgramConfigByName(selectedEpisode!.program_name)?.noteContentMetadataName ?? {}
          // Prefer suggestedSessionType when present; otherwise fall back to sessionNumber. Resolve the
          // program the same way the charge does (CPR-2213) so LCT episodes classify against DA types.
          const isInitialEvaluation =
            isInitialEvaluationSessionType(
              appointmentToClose.suggestedSessionType,
              resolveDAEngageProgramName(selectedEpisode!.program_name, isProviderEngageDA),
            ) ?? appointmentToClose.sessionNumber <= 1
          const contentMetaDataSearch = isInitialEvaluation ? INITIAL_NOTE : SESSION_NOTE
          const contentMetadata = contents.find((content) => content.name === contentMetaDataSearch)

          const appointmentTimeslot = getAppointmentTimeslot({ appointment: appointmentToClose, intl })

          const body = populateInitialValues([], contentMetaDataSearch, contents)

          let noteToNavigateTo
          if (draftNote) {
            noteToNavigateTo = draftNote
          } else {
            noteToNavigateTo = {
              title: `Session ${appointmentToClose.sessionNumber} Note`,
              appointment_id: appointmentToClose.appointmentId,
              content_meta_data: contentMetadata,
              note_type: isInitialEvaluation ? NoteTypes.INITIAL : NoteTypes.SESSION,
              sessionDate: appointmentToClose.startDate,
              sessionNumber: appointmentToClose.sessionNumber,
              appointmentTimeslot,
              body,
            }
          }

          if (!chargeId) {
            logToSentry('daNotesWorkflow', {
              message: 'DA notes workflow: close session navigation without charge ID',
              charge,
            })
          }

          navigate(`${CLIENT_NEW_NOTE.route}`, {
            state: {
              note: { ...noteToNavigateTo },
              chargeId,
              needToUpdateCharge: shouldCharge,
              originRoute: originRouteFromState,
            },
          })
        } else {
          navigate(-1)
        }
      }

      if (closedOutOfOrder) {
        if (!hasNoShowTransfersError && !isProviderEngageDA) {
          setAlertBanner(
            <AlertBanner
              dismissHandler={() => setAlertBanner(null)}
              includeX={true}
              key={`alertSuccess`}
              show={!!alertBanner}
              style={AlertBannerTypes.SUCCESS}
              contents={`Session ${appointmentToClose.sessionNumber} has been closed`}
              autoDismissTimer={4000}
            />,
          )
        }
      }

      return charge
    } catch (error: unknown) {
      const closeSessionError = error as CloseSessionError
      logToSentry(
        'closeSession',
        {
          step: 'Error closing session',
          clientId: clientDetails?.id,
          errorMessage: closeSessionError?.message,
        },
        'error',
      )
      setAlertBanner(
        <AlertBanner
          dismissHandler={() => setAlertBanner(null)}
          includeX={true}
          key={`alertSubmission`}
          show={true}
          style={AlertBannerTypes.DANGER}
          contents={
            closeSessionError?.response?.data?.message || 'There was an error closing the session. Please try again.'
          }
        />,
      )
    }
  }

  const isLastSessionHandler = ({ input: { value } }: any) => {
    if (!isBoolean(value)) {
      return null
    }
    let notificationBanner
    const currentEpisodeUpcomingAppointments =
      allAppointments?.filter((appt: any) => appt.episodeId === selectedEpisode?.id && !appt.pastAppointment) ?? []
    const params = new URLSearchParams(location.search)
    const relId = params.get('relId')
    if (value) {
      const link = (
        <Link
          text={'Cancel any upcoming sessions'}
          onPress={() => window.open(`${CLIENTS_SESSIONS.route}?relId=${relId}`, '_blank')}
          testID={tID(`EndOfSession-cancelUpcomingSessionsLink`)}
          size={BodyTextSize.DEFAULT}
        />
      )
      notificationBanner = (
        <InlineWarningBanner
          text={
            isDAProviderMigrated ? (
              <>
                Once you discharge this client, they will be removed from your active client list and you won&apos;t be
                able to schedule any more sessions. Submit all past session details and invoices, and cancel any
                upcoming sessions before continuing.
              </>
            ) : (
              <>
                Once you end an episode, the client will be graduated from care and you won&apos;t be able to schedule
                any more sessions. {link} to avoid seeing unfinished notes.
              </>
            )
          }
          backgroundColor={colors.backgroundSection}
        />
      )
    } else {
      if (programConfig?.autoAssignOnSessionClose) {
        const link = relId ? (
          <Link
            text={'View upcoming session activities'}
            onPress={() => window.open(`${CLIENT_ACTIVITIES.route}?relId=${relId}`, '_blank')}
            testID={tID(`EndOfSession-sessionClosurePolicyLink`)}
            size={BodyTextSize.DEFAULT}
          />
        ) : undefined
        const notificationText = `Ensure all activities for the next session are assigned. They'll be shared with your client once you close this session.`

        notificationBanner = (
          <InlineWarningBanner
            text={
              <>
                {notificationText} {link}
              </>
            }
          />
        )
      }
    }
    return (
      <NotificationBannerContainer>
        {notificationBanner}
        {value && currentEpisodeUpcomingAppointments.length > 0 && (
          <CloseSessionWarningText
            style={{ marginTop: 15 }}
            testID={tID('EndOfSession-closeUpcomingSessionBanner')}
            text='If you have upcoming sessions when you close the episode, cancel them to avoid seeing unfinished notes.'
          />
        )}
      </NotificationBannerContainer>
    )
  }

  const renderHealthPlanLimitInfo = () => (
    <BodyText testID={tID('EndOfSession-HealthPlanLimitInfo')} text={healthPlanLimitInfoText} />
  )

  const customValidationFunction = useCallback((fields: any, values: any) => {
    let errors = {}
    // @ts-expect-error TS(2339): Property 'items' does not exist on type 'unknown'.
    Object.entries(fields).forEach(([, { items, condition }]) => {
      // @ts-expect-error TS(2345): Argument of type '{ condition: any; formValues: an... Remove this comment to see the full error message
      const isDisplayed = isFieldDisplayed({ condition, formValues: values, fields })
      if (items) {
        const fields = items.reduce(
          // @ts-expect-error TS(7006): Parameter 'obj' implicitly has an 'any' type.
          (obj, item) => ({
            [item.name]: item,
            ...obj,
          }),
          {},
        )
        const isOneSelected = isOneArrayItemTrue(fields, values)
        if (!isOneSelected && isDisplayed) {
          errors = merge(errors, getItemsErrors(fields))
        }
      }
    })
    return errors
  }, [])

  const customValuesListener = useCallback(({ values }: { values: { [key: string]: FieldValue } }) => {
    if (!isNil(values) && !isEmpty(values)) {
      setClosureFormValues((prevValues: endOfSessionMetadataValues): endOfSessionMetadataValues => {
        if (!isEqual(prevValues, values)) {
          return values as unknown as endOfSessionMetadataValues
        }
        return prevValues
      })
    }
  }, [])

  const appointmentStartZonedToUTC =
    appointmentToClose?.startDate &&
    appointmentToClose?.startTime &&
    zonedTimeToUtc(
      new Date(`${appointmentToClose?.startDate} ${appointmentToClose?.startTime}`),
      appointmentToClose?.timeZone ?? 'America/Los_Angeles',
    )
  const appointmentDate = appointmentStartZonedToUTC && format(appointmentStartZonedToUTC, 'EEEE, MMM d, h:mm a zzz')

  const sessionOlderThan90Days = isSessionOlderThan90Days(appointmentToClose)

  const scrollToFirstErrorIfNeeded = useCallback(() => {
    setTimeout(() => {
      document
        ?.querySelector('[role=alert]')
        ?.parentElement?.parentElement?.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' })
    }, 100)
  }, [])

  const submitButtonText = isProviderEngageDA
    ? intl.formatMessage({
        defaultMessage: 'Submit',
        description: 'Button label to submit the form',
      })
    : intl.formatMessage({
        defaultMessage: 'Close Session',
        description: 'Button label to submit the form',
      })

  const cancelButtonText = isProviderEngageDA
    ? intl.formatMessage({
        defaultMessage: 'Exit',
        description: 'Button label to submit the form',
      })
    : intl.formatMessage({
        defaultMessage: 'Cancel',
        description: 'Button label to submit the form',
      })

  const headerTextOverride = useMemo(() => {
    const sessionNumberBeingClosed =
      showClosingSessionsOutOfOrderAlert &&
      !isNil(originallyTargetedAppointment) &&
      !isEmpty(originallyTargetedAppointment) &&
      !isProviderEngageDA
        ? originallyTargetedAppointment.sessionNumber
        : appointmentToClose.sessionNumber
    if (!isProviderEngageDA) {
      return `Close Session ${sessionNumberBeingClosed} with ${clientDetails?.first_name}`
    }

    const clientPreferredName = getClientPreferredNameIfExists(clientDetails) || clientDetails?.full_name
    return `Complete invoice for session ${sessionNumberBeingClosed} for ${clientPreferredName}`
  }, [
    showClosingSessionsOutOfOrderAlert,
    originallyTargetedAppointment,
    appointmentToClose.sessionNumber,
    isProviderEngageDA,
    clientDetails,
  ])

  return isLoading || !clientDetails ? (
    <LoadingIndicator size={45} />
  ) : (
    <WholeContainer>
      <LeftSideContainer>
        {isFormProcessing ? (
          <InnerContainer isFormProcessing={isFormProcessing}>
            <Subheader
              size={TitleSize.LARGE}
              text={
                isProviderEngageDA
                  ? `Submitting session ${appointmentToClose.sessionNumber} invoice for ${clientDetails?.first_name}`
                  : `Closing Session with ${clientDetails?.first_name}`
              }
              color={colors.textPrimary}
            />
            <BodyText text='Please do not navigate away from or close this page' />
            <LoadingIndicator size={45} />
          </InnerContainer>
        ) : (
          <>
            <StickyHeaderContainer topOffset={topOffset + activeAlertTopOffset}>
              <ClientDetailsHeader
                clientDetails={clientDetails ?? null}
                handleExit={() => {
                  setShowSubNav(true)
                  navigate(-1)
                }}
                exitButtonTextOverride={cancelButtonText}
                setIsTabbedExpandingSidePanelOpen={setIsTabbedExpandingSidePanelOpen}
                noteIsBeingAutoSaved={false}
                noteStatus={NoteStatusStates.DRAFT}
                noteType={NoteTypes.SESSION}
                sessionNumber={`${
                  showClosingSessionsOutOfOrderAlert &&
                  !isNil(originallyTargetedAppointment) &&
                  !isEmpty(originallyTargetedAppointment) &&
                  !isProviderEngageDA
                    ? originallyTargetedAppointment.sessionNumber
                    : appointmentToClose.sessionNumber
                }`}
                showSubmit
                submitButton={() => (
                  <SubmitButton
                    disabled={
                      (sessionOlderThan90Days && isProviderEngageDA) ||
                      (showClosingSessionsOutOfOrderAlert &&
                        !isNil(originallyTargetedAppointment) &&
                        !isEmpty(originallyTargetedAppointment) &&
                        originallyTargetedAppointment.appointmentId !== appointmentToClose.appointmentId &&
                        !isProviderEngageDA)
                    }
                    loading={isFormProcessing}
                    testID={tID('EndOfSession-closeSessionButton')}
                    onPress={() => {
                      submitRef.current?.()
                      const errors = endOfSessionFormRef.current?.getState?.().errors
                      endOfSessionFormRef.current?.mutators?.setTouched?.(errors)
                      if (!isEmpty(errors)) {
                        scrollToFirstErrorIfNeeded()
                      } else {
                        submitForm()
                      }
                    }}
                    text={submitButtonText}
                    style={{ marginRight: 16 }}
                  />
                )}
                submitDate={null}
                updateDate={new Date().toISOString()}
                isSideBarIconVisible={!isTabbedExpandingSidePanelOpen}
                headerTextOverride={headerTextOverride}
                secondaryTextOverride={appointmentDate || null}
                hasSidePanelToggle={isEndOfSessionSidePanelEnabled}
              />
            </StickyHeaderContainer>
            <AllAlertsContainer videoSessionOpen={hasVideoSessionBanner}>
              {!!alertBanner && <SubmissionAlertContainer>{alertBanner}</SubmissionAlertContainer>}

              {showClosingSessionsOutOfOrderAlert && !isProviderEngageDA && (
                <OutOfOrderAlertContainer
                  submissionAlertBanner={!!alertBanner}
                  testID={tID('EndOfSession-alertContainer')}
                >
                  <AlertBanner
                    dismissHandler={() => setShowClosingSessionsOutOfOrderAlert(false)}
                    includeX={false}
                    key={`alertClosingSessionOutOfOrder`}
                    show={showClosingSessionsOutOfOrderAlert}
                    style={AlertBannerTypes.DANGER}
                    contents={
                      <>
                        <Outlet
                          context={{
                            appointment: appointmentToClose,
                          }}
                        />
                        <>You must close all previous sessions with this client first. </>
                        <Link
                          onPress={() => {
                            setShowClosingSessionsOutOfOrderAlert(false)
                          }}
                          text={`Close session ${appointmentToClose.sessionNumber}`}
                          style={{ fontSize: 18, textDecorationLine: 'underline' }}
                        />
                      </>
                    }
                  />
                </OutOfOrderAlertContainer>
              )}
            </AllAlertsContainer>
            <InnerContainer>
              {(showLowSessionsWarning || showLowLicenseSessionWarning) && (
                <InlineWarningBanner
                  text={
                    <BodyText
                      // License should take precedent since it will only be true for license based programs
                      text={showLowLicenseSessionWarning ? lowLicenseSessionRemainingText : lowSessionsRemainingText}
                      size={BodyTextSize.DEFAULT}
                      color={colors.components.inlineWarningBanner.text}
                    />
                  }
                  testId={tID('EndOfSession-LowSessionsRemainingWarning')}
                />
              )}
              {eligibilityError && <EligibilityErrorBanner />}
              {sessionOlderThan90Days && isProviderEngageDA && (
                <InlineWarningBanner
                  text={
                    <BodyText
                      text='Sessions older than 90 days cannot be submitted.'
                      size={BodyTextSize.DEFAULT}
                      color={colors.textWarning}
                    />
                  }
                  testId={tID('EndOfSession-SessionOlderThan90DaysWarning')}
                />
              )}
              <EndOfSessionDAPreText />
              {!isProviderEngageDA && (
                <BillingImplicationsMessage
                  appointment={appointmentToClose}
                  clientDetails={clientDetails}
                  programConfig={programConfig}
                />
              )}
              {metadata && (
                <FormBody
                  customValuesListener={customValuesListener}
                  intl={intl}
                  schema={metadata.schema as FieldSchema}
                  uiSchema={metadata.uiSchema}
                  initialValues={!isEmpty(closureFormValues) ? closureFormValues : metadata.initialValues}
                  customFields={{
                    icd10s: (props: any) => (
                      <ICD10sField {...props} availableCodes={icd10Codes} variant={icd10sVariant} />
                    ),
                  }}
                  customValidation={customValidationFunction}
                  saveForm={noop}
                  name='endOfSession'
                  handleSubmitRef={submitRef}
                  formRef={endOfSessionFormRef}
                  scrollContainerCustomStyles={{
                    formBodyPageContainer: css`
                      background-color: ${colors.backgroundPrimary};
                      padding-top: 0px;
                      max-width: 720px;
                      margin: 0;
                      padding: 0;
                    `,
                    scrollContainerStyle: {
                      margin: 0,
                      padding: 0,
                    },
                    scrollContainerContentCustomStyles: {
                      padding: 0,
                      margin: 0,
                    },
                  }}
                  formBodyCustomStyles={
                    {
                      backgroundColor: colors.backgroundPrimary,
                      submitButtonContainer: {
                        borderTopWidth: 0,
                        padding: 0,
                        width: '100%',
                        boxShadow: 'none',
                        background: 'transparent',
                      },
                    } as unknown as FormBodyCustomStyles
                  }
                  formContext={{ useMultiSelectTypeAhead: true, saveSingleSelectFieldsAsArray: false }}
                >
                  {showHealthPlanLimitInfo && renderHealthPlanLimitInfo()}
                  <Field name='is_last_session' subscription={{ value: true }} render={isLastSessionHandler} />
                </FormBody>
              )}
            </InnerContainer>
          </>
        )}
      </LeftSideContainer>
      {isEndOfSessionSidePanelEnabled && (
        <RightSideContainer testID={tID('EndOfSession-rightSideContainer')} topOffset={topOffset}>
          <TabbedExpandingSidePanel
            isOpen={isTabbedExpandingSidePanelOpen}
            onClose={() => setIsTabbedExpandingSidePanelOpen(false)}
            selectedTab={tabbedExpandingSidePanelContent.selectedTab}
            setSelectedTab={tabbedExpandingSidePanelContent.setSelectedTab}
            tabs={tabbedExpandingSidePanelContent.tabs}
            transitionTime={SIDEBAR_TRANSITION_TIME}
            contentHeightOffset={topOffset + activeAlertTopOffset}
            hoveredTabId={tabbedExpandingSidePanelContent.hoveredTabId}
            setHoveredTabId={tabbedExpandingSidePanelContent.setHoveredTabId}
          />
        </RightSideContainer>
      )}
    </WholeContainer>
  )
}

type EndOfSessionProps = ConnectedProps<typeof connector>

const mapDispatchToProps = (dispatch: any) => ({
  actions: bindActionCreators(
    {
      getHPEligibility,
      getICD10s,
      getPayments,
      getContentsByProgramId,
    },
    dispatch,
  ),
})

const connector = connect(null, mapDispatchToProps)

export default connector(EndOfSession)
